import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  NumberField,
  Switch,
  TextArea,
  TextField,
} from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { createPortal } from "react-dom";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { FormError, PlainCheckbox, notify } from "@/components/kit";
import { useUpdateAppSettings } from "@/hooks/api/app-settings/useUpdateAppSettings";
import {
  APP_SETTINGS_FORM_SCHEMA,
  AppSettingsFormData,
} from "@/schemas/app-settings-form.schema";
import { AppSettings } from "@/types";
import { getApiErrorMessage } from "@/utils/api-error";
import { formatFullDate } from "@/utils/date";
import { pickDirtyValues } from "@/utils/form";

type PricingField =
  | "minimumOrderAmount"
  | "deliveryFee"
  | "deliveryCutOff"
  | "scheduledMinimumOrderAmount"
  | "scheduledDeliveryFee"
  | "scheduledDeliveryCutOff"
  | "codExtraFee";

type PricingFieldConfig = {
  name: PricingField;
  label: string;
  description: string;
};

const PRICING_FIELDS: PricingFieldConfig[] = [
  {
    name: "minimumOrderAmount",
    label: "Minimum order amount",
    description: "Orders below this subtotal cannot be placed",
  },
  {
    name: "deliveryFee",
    label: "Delivery fee",
    description: "Charged when the subtotal is below the free delivery cut-off",
  },
  {
    name: "deliveryCutOff",
    label: "Free delivery cut-off",
    description: "Subtotals at or above this get free delivery",
  },
  {
    name: "codExtraFee",
    label: "COD fee",
    description: "Added to cash-on-delivery orders",
  },
];

const SCHEDULED_PRICING_FIELDS: PricingFieldConfig[] = [
  {
    name: "scheduledMinimumOrderAmount",
    label: "Minimum order amount",
    description: "Scheduled deliveries below this subtotal cannot be placed",
  },
  {
    name: "scheduledDeliveryFee",
    label: "Delivery fee",
    description: "Charged when the subtotal is below the free delivery cut-off",
  },
  {
    name: "scheduledDeliveryCutOff",
    label: "Free delivery cut-off",
    description: "Subtotals at or above this get free delivery",
  },
];

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const toFormValues = (settings: AppSettings): AppSettingsFormData => ({
  minAppVersion: settings.minAppVersion,
  localBrandsEnabled: settings.localBrandsEnabled,
  generalNotice: settings.generalNotice,
  generalNoticeAr: settings.generalNoticeAr,
  minimumOrderAmount: settings.minimumOrderAmount,
  deliveryFee: settings.deliveryFee,
  deliveryCutOff: settings.deliveryCutOff,
  scheduledMinimumOrderAmount: settings.scheduledMinimumOrderAmount,
  scheduledDeliveryFee: settings.scheduledDeliveryFee,
  scheduledDeliveryCutOff: settings.scheduledDeliveryCutOff,
  codExtraFee: settings.codExtraFee,
  sameDayDeliveryEnabled: settings.sameDayDeliveryEnabled,
  sameDayCutoff: settings.sameDayCutoff,
  deliveryOffDays: settings.deliveryOffDays,
});

/** A settings group: its name and what it is for on the left, the fields on the right. */
const SettingsSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <section className="border-separator grid grid-cols-1 gap-x-8 gap-y-4 border-b py-8 first:pt-2 last:border-b-0 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
    <div className="flex flex-col gap-1">
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-muted max-w-xs text-sm">{description}</p>
    </div>
    <div className="flex max-w-2xl flex-col gap-5">{children}</div>
  </section>
);

const PricingFields = ({ fields }: { fields: PricingFieldConfig[] }) => {
  const { control } = useFormContext<AppSettingsFormData>();

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
      {fields.map(({ name, label, description }) => (
        <Controller
          key={name}
          control={control}
          name={name}
          render={({ field, fieldState }) => (
            <NumberField
              isRequired
              className="flex flex-col gap-1.5"
              formatOptions={{ style: "currency", currency: "AED" }}
              isInvalid={fieldState.invalid}
              minValue={0}
              name={field.name}
              value={field.value}
              variant="secondary"
              onBlur={field.onBlur}
              onChange={field.onChange}
            >
              <Label>{label}</Label>
              <NumberField.Group>
                <NumberField.Input />
              </NumberField.Group>
              <Description>{description}</Description>
              <FieldError>{fieldState.error?.message}</FieldError>
            </NumberField>
          )}
        />
      ))}
    </div>
  );
};

interface AppSettingsFormProps {
  settings: AppSettings;
  /** The page header's action area: Save changes is drawn there. */
  actionSlot: HTMLElement | null;
}

const FORM_ID = "app-settings-form";

export const AppSettingsForm = ({
  settings,
  actionSlot,
}: AppSettingsFormProps) => {
  const form = useForm({
    resolver: zodResolver(APP_SETTINGS_FORM_SCHEMA),
    mode: "all",
    defaultValues: toFormValues(settings),
  });

  // Reading dirtyFields while rendering subscribes to it: react-hook-form 7.76 swaps the object on every edit, so a
  // handler that reads form.formState.dirtyFields without subscribing sees only the first edited field.
  const { dirtyFields, isDirty } = form.formState;

  const sameDayDeliveryEnabled = useWatch({
    control: form.control,
    name: "sameDayDeliveryEnabled",
  });

  const {
    mutate: updateSettings,
    isPending: isSaving,
    error: saveError,
    variables: savedValues,
  } = useUpdateAppSettings();

  const handleSubmit = (data: AppSettingsFormData) => {
    updateSettings(pickDirtyValues(data, dirtyFields), {
      onSuccess: (updated) => {
        form.reset(toFormValues(updated));
        notify.success("App settings saved");
      },
      onError: (error) => {
        notify.error(
          "Failed to save app settings",
          getApiErrorMessage(error, "Please try again"),
        );
      },
    });
  };

  return (
    <FormProvider {...form}>
      {actionSlot
        ? createPortal(
            <>
              <span className="text-muted hidden text-sm sm:inline">
                Last updated {formatFullDate(settings.updatedAt)}
              </span>
              <Button
                form={FORM_ID}
                isDisabled={!isDirty || isSaving}
                isPending={isSaving}
                type="submit"
              >
                Save changes
              </Button>
            </>,
            actionSlot,
          )
        : null}
      <form
        noValidate
        className="pb-6"
        id={FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit(handleSubmit)();
        }}
      >
        <SettingsSection
          description="Version requirements and feature flags for the mobile app"
          title="App"
        >
          <Controller
            control={form.control}
            name="minAppVersion"
            render={({ field, fieldState }) => (
              <TextField
                isRequired
                className="flex max-w-xs flex-col gap-1.5"
                isInvalid={fieldState.invalid}
                name={field.name}
                value={field.value}
                onBlur={field.onBlur}
                onChange={field.onChange}
              >
                <Label>Minimum app version</Label>
                <Input
                  ref={field.ref}
                  placeholder="e.g. 5.3.0"
                  variant="secondary"
                />
                <Description>
                  Apps below this version are forced to update
                </Description>
                <FieldError>{fieldState.error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={form.control}
            name="localBrandsEnabled"
            render={({ field }) => (
              <Switch
                isSelected={field.value}
                onBlur={field.onBlur}
                onChange={field.onChange}
              >
                <Switch.Content>
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium">Local brands</span>
                    <span className="text-muted text-xs">
                      Show the local brands section in the app
                    </span>
                  </span>
                </Switch.Content>
              </Switch>
            )}
          />
        </SettingsSection>

        <SettingsSection
          description="Shown to all app users, leave empty to hide the notice"
          title="Notice"
        >
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Controller
              control={form.control}
              name="generalNotice"
              render={({ field, fieldState }) => (
                <TextField
                  className="flex flex-col gap-1.5"
                  isInvalid={fieldState.invalid}
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                >
                  <Label>Notice (English)</Label>
                  <TextArea
                    ref={field.ref}
                    placeholder="Enter notice"
                    rows={4}
                    variant="secondary"
                  />
                  <FieldError>{fieldState.error?.message}</FieldError>
                </TextField>
              )}
            />
            <Controller
              control={form.control}
              name="generalNoticeAr"
              render={({ field, fieldState }) => (
                <TextField
                  className="flex flex-col gap-1.5"
                  isInvalid={fieldState.invalid}
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                >
                  <Label>Notice (Arabic)</Label>
                  <TextArea
                    ref={field.ref}
                    dir="rtl"
                    lang="ar"
                    placeholder="أدخل الإشعار"
                    rows={4}
                    variant="secondary"
                  />
                  <FieldError>{fieldState.error?.message}</FieldError>
                </TextField>
              )}
            />
          </div>
        </SettingsSection>

        <SettingsSection
          description="Order limits and fees, all amounts in AED"
          title="Order pricing"
        >
          <PricingFields fields={PRICING_FIELDS} />
        </SettingsSection>

        <SettingsSection
          description="Used instead of order pricing for deliveries scheduled after the store date, all amounts in AED"
          title="Scheduled delivery pricing"
        >
          <PricingFields fields={SCHEDULED_PRICING_FIELDS} />
        </SettingsSection>

        <SettingsSection
          description="Orders placed before the cut-off, Dubai time, are delivered the same day"
          title="Same-day delivery"
        >
          <Controller
            control={form.control}
            name="sameDayDeliveryEnabled"
            render={({ field }) => (
              <Switch
                isSelected={field.value}
                onBlur={field.onBlur}
                onChange={field.onChange}
              >
                <Switch.Content>
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium">
                      Same-day delivery
                    </span>
                    <span className="text-muted text-xs">
                      Turn off to deliver every order the next day
                    </span>
                  </span>
                </Switch.Content>
              </Switch>
            )}
          />
          <Controller
            control={form.control}
            name="sameDayCutoff"
            render={({ field, fieldState }) => (
              <TextField
                isRequired
                className="flex max-w-xs flex-col gap-1.5"
                isDisabled={!sameDayDeliveryEnabled}
                isInvalid={fieldState.invalid}
                name={field.name}
                type="time"
                value={field.value}
                onBlur={field.onBlur}
                onChange={field.onChange}
              >
                <Label>Cut-off time</Label>
                <Input ref={field.ref} variant="secondary" />
                <Description>Dubai time</Description>
                <FieldError>{fieldState.error?.message}</FieldError>
              </TextField>
            )}
          />
        </SettingsSection>

        <SettingsSection
          description="Weekdays with delivery, Dubai time. Delivery estimates skip the unticked days"
          title="Delivery days"
        >
          <Controller
            control={form.control}
            name="deliveryOffDays"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {WEEKDAYS.map((weekday, index) => (
                    <PlainCheckbox
                      key={weekday}
                      isSelected={!field.value.includes(index)}
                      label={weekday}
                      onChange={(isDeliveryDay) =>
                        field.onChange(
                          isDeliveryDay
                            ? field.value.filter((day) => day !== index)
                            : [...field.value, index].sort((a, b) => a - b),
                        )
                      }
                    />
                  ))}
                </div>
                {fieldState.error?.message ? (
                  <p className="text-sm text-(--danger)" role="alert">
                    {fieldState.error.message}
                  </p>
                ) : null}
                <FormError
                  message={
                    saveError && savedValues?.deliveryOffDays !== undefined
                      ? getApiErrorMessage(
                          saveError,
                          "Could not save the delivery days",
                        )
                      : null
                  }
                />
              </div>
            )}
          />
        </SettingsSection>
      </form>
    </FormProvider>
  );
};
